/**
 * Pricing Community Assessment Service
 * Adapts the existing community assessment flow to save to pricing_assessments table
 */

import { montanaHOAFormService } from '../hoa/montanaHOAFormService';
import type { CommunityAssessmentData } from '@/types/entities/clients/assessment';

class PricingCommunityAssessmentService {
  /**
   * Submit assessment to pricing_assessments table
   */
  async submitAssessment(data: CommunityAssessmentData): Promise<void> {
    console.log('🏔️ Submitting pricing assessment to PostgreSQL (DROPLET)');

    const hoaPricingData = {
      selected_tier: data.selectedTier,
      community_name: data.communityName,
      total_units: data.totalUnits || 'TBD',
      community_type: data.communityType || 'TBD',
      contact_name: data.contactName,
      contact_email: data.contactEmail,
      contact_phone: data.contactPhone,
      selected_addons: data.selectedAddOns,
      budget_range: data.financialPosition?.annualBudget?.toString(),
      urgency: data.implementationReadiness?.urgencyReasons,
      decision_process: data.decisionMakingProcess?.budgetApprovalProcess,
      wishlist_features: { goals: data.primaryGoals || [] },
      goals: { primary: data.primaryGoals || [] },
      success_metrics: { criteria: data.implementationReadiness?.successCriteria || [] },
      source: 'pricing-wizard',
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    };

    const response = await montanaHOAFormService.submitPricingAssessment(hoaPricingData);

    if (!response.success) {
      throw new Error(response.message || 'Failed to submit pricing assessment to PostgreSQL');
    }

    // Persist stakeholders if present
    if (data.keyStakeholders && data.keyStakeholders.length && response.data?.id) {
      for (const stakeholder of data.keyStakeholders) {
        await montanaHOAFormService.submitStakeholderContact({
          assessment_id: response.data.id as string,
          name: stakeholder.name,
          email: stakeholder.email,
          title: stakeholder.title,
          role: stakeholder.role,
        });
      }
    }

    console.log('Pricing assessment submitted successfully to PostgreSQL');
  }

  /**
   * Save progress
   */
  saveProgress(data: Partial<CommunityAssessmentData>, step: number): void {
    localStorage.setItem('pricingAssessmentProgress', JSON.stringify({ data, step }));
  }

  /**
   * Load progress
   */
  loadProgress(): { data: Partial<CommunityAssessmentData>; step: number } | null {
    const saved = localStorage.getItem('pricingAssessmentProgress');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Failed to parse saved pricing assessment progress:', error);
      }
    }
    return null;
  }

  /**
   * Clear progress
   */
  clearProgress(): void {
    localStorage.removeItem('pricingAssessmentProgress');
  }
}

export const pricingCommunityAssessmentService = new PricingCommunityAssessmentService();
